import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Mail,
  Phone,
  Globe,
  Loader2,
  UserCheck,
  UserX,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminCollaborators = () => {
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCollab, setEditingCollab] = useState(null);
  const [saving, setSaving] = useState(false);

  const emptyForm = {
    email: "",
    password: "",
    name: "",
    bio: { fr: "", en: "" },
    specialty: "yoga",
    phone: "",
    website: "",
  };

  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    fetchCollaborators();
  }, []);

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
  });

  const fetchCollaborators = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/collaborators`, getAuthHeader());
      setCollaborators(res.data);
    } catch (err) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (collab) => {
    setEditingCollab(collab);
    setForm({
      email: collab.email,
      password: "", // Don't show password
      name: collab.name,
      bio: collab.bio || { fr: "", en: "" },
      specialty: collab.specialty || "yoga",
      phone: collab.phone || "",
      website: collab.website || "",
    });
    setShowForm(true);
  };

  const handleCreate = () => {
    setEditingCollab(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.email || !form.name) {
      toast.error("Email et nom requis");
      return;
    }

    if (!editingCollab && !form.password) {
      toast.error("Mot de passe requis pour un nouveau collaborateur");
      return;
    }

    try {
      setSaving(true);
      if (editingCollab) {
        const updateData = { ...form };
        if (!updateData.password) delete updateData.password;
        delete updateData.email; // Can't change email
        
        await axios.put(
          `${API}/admin/collaborators/${editingCollab.id}`,
          updateData,
          getAuthHeader()
        );
        toast.success("Collaborateur mis à jour");
      } else {
        await axios.post(`${API}/admin/collaborators`, form, getAuthHeader());
        toast.success("Collaborateur créé");
      }
      setShowForm(false);
      fetchCollaborators();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (collabId) => {
    if (!window.confirm("Désactiver ce collaborateur ?")) return;
    try {
      await axios.delete(`${API}/admin/collaborators/${collabId}`, getAuthHeader());
      toast.success("Collaborateur désactivé");
      fetchCollaborators();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const specialties = [
    { id: "yoga", name: "Yoga" },
    { id: "meditation", name: "Méditation" },
    { id: "art", name: "Art & Créativité" },
    { id: "music", name: "Musique" },
    { id: "wellness", name: "Bien-être" },
    { id: "spiritual", name: "Spiritualité" },
  ];

  return (
    <div data-testid="admin-collaborators-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-serif font-bold">Collaborateurs</h1>
          <p className="text-muted-foreground">
            Gérez les organisateurs de stages et retraites
          </p>
        </div>
        <Button onClick={handleCreate} data-testid="add-collaborator-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nouveau collaborateur
        </Button>
      </div>

      {/* Info Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-8">
        <p className="text-sm text-blue-700">
          Les collaborateurs peuvent se connecter à leur espace dédié pour créer et gérer leurs propres stages et retraites. 
          Les stages créés par les collaborateurs nécessitent une approbation admin avant publication.
        </p>
      </div>

      {/* Collaborators List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : collaborators.length === 0 ? (
        <div className="text-center py-12 bg-muted/30 rounded-xl">
          <UserCheck className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">Aucun collaborateur</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {collaborators.map((collab) => (
            <div
              key={collab.id}
              className={`bg-card border rounded-xl p-6 ${
                !collab.is_active ? "opacity-50" : ""
              }`}
              data-testid={`collaborator-${collab.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <span className="text-primary font-bold text-lg">
                      {collab.name?.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-semibold">{collab.name}</h3>
                    <Badge variant="outline">{collab.specialty}</Badge>
                  </div>
                </div>
                {collab.is_active ? (
                  <Badge className="bg-green-500">
                    <UserCheck className="h-3 w-3 mr-1" />
                    Actif
                  </Badge>
                ) : (
                  <Badge variant="secondary">
                    <UserX className="h-3 w-3 mr-1" />
                    Inactif
                  </Badge>
                )}
              </div>

              <div className="space-y-2 text-sm text-muted-foreground mb-4">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  <span>{collab.email}</span>
                </div>
                {collab.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    <span>{collab.phone}</span>
                  </div>
                )}
                {collab.website && (
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    <a
                      href={collab.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      Site web
                    </a>
                  </div>
                )}
              </div>

              {collab.bio?.fr && (
                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                  {collab.bio.fr}
                </p>
              )}

              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1"
                  onClick={() => handleEdit(collab)}
                >
                  <Pencil className="h-4 w-4 mr-1" />
                  Modifier
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-destructive"
                  onClick={() => handleDelete(collab.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit/Create Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingCollab ? "Modifier le collaborateur" : "Nouveau collaborateur"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid gap-4">
            {/* Name */}
            <div>
              <Label>Nom complet *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>

            {/* Email */}
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                disabled={!!editingCollab}
              />
            </div>

            {/* Password */}
            <div>
              <Label>
                Mot de passe {editingCollab ? "(laisser vide pour ne pas changer)" : "*"}
              </Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            </div>

            {/* Specialty */}
            <div>
              <Label>Spécialité</Label>
              <Select
                value={form.specialty}
                onValueChange={(v) => setForm({ ...form, specialty: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {specialties.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Phone */}
            <div>
              <Label>Téléphone</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>

            {/* Website */}
            <div>
              <Label>Site web</Label>
              <Input
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                placeholder="https://..."
              />
            </div>

            {/* Bio */}
            <div>
              <Label>Bio (FR)</Label>
              <Textarea
                value={form.bio.fr}
                onChange={(e) =>
                  setForm({ ...form, bio: { ...form.bio, fr: e.target.value } })
                }
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>
              Annuler
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingCollab ? "Mettre à jour" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCollaborators;
